import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LockKeyhole, UserRound, Eye, EyeOff } from "lucide-react";
import {
  apiClient,
  ENDPOINTS,
  useAuthStore,
  type ApiError,
  type Token,
  type User,
} from "@iziping/common";
import InputIcon from "../InputIcon/InputIcon";
import styles from "./Auth.module.css";

interface LoginPageProps {
  logo?: string;
  googleClientId?: string;
  title?: string;
  subtitle?: string;
  footerText?: string;
  footerLinkText?: string;
  footerLinkHref?: string;
}

export function LoginPage({
  logo,
  googleClientId,
  title,
  subtitle,
  footerText,
  footerLinkText,
  footerLinkHref,
}: LoginPageProps) {
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const googleButtonRef = useRef<HTMLDivElement>(null);

  const storeTokenAndNavigate = async (token: Token) => {
    useAuthStore.getState().setAccessToken(token.accessToken);
    useAuthStore.getState().setRefreshToken(token.refreshToken);
    const userCall = await apiClient.get<User>(ENDPOINTS.me);
    useAuthStore.getState().setUser(userCall.data);
    navigate("/");
  };

  const handleLogin = async () => {
    try {
      const response = await apiClient.post<Token>(ENDPOINTS.login, {
        username,
        password,
      });
      await storeTokenAndNavigate(response.data);
    } catch (err) {
      const apiError = err as ApiError;
      if (apiError.error === "network.error") {
        setError(t("SERVER_UNAVAILABLE"));
      } else if (apiError.error === "account.locked") {
        setError(t("login.locked", t("login.invalid")));
      } else {
        setError(t("login.invalid"));
      }
    }
  };

  const handleGoogleCredential = async (credential: string) => {
    setError(null);
    try {
      const response = await apiClient.post<Token>(ENDPOINTS.googleLogin, {
        credential,
      });
      await storeTokenAndNavigate(response.data);
    } catch (err) {
      const apiError = err as ApiError;
      if (apiError.error === "google.account.not.linked") {
        setError(t("login.google.notLinked"));
      } else if (apiError.error === "network.error") {
        setError(t("SERVER_UNAVAILABLE"));
      } else {
        setError(t("login.google.error"));
      }
    }
  };

  useEffect(() => {
    if (!googleClientId || !googleButtonRef.current) {
      return;
    }

    const initGoogle = () => {
      const g = (window as Window & { google?: GoogleAccounts }).google;
      if (!g) {
        return;
      }
      g.accounts.id.initialize({
        client_id: googleClientId,
        callback: (response: { credential: string }) => {
          handleGoogleCredential(response.credential);
        },
      });
      g.accounts.id.renderButton(googleButtonRef.current!, {
        theme: "outline",
        size: "large",
        width: googleButtonRef.current!.offsetWidth || 300,
        text: "continue_with",
        locale: "fr",
      });
    };

    if ((window as Window & { google?: GoogleAccounts }).google) {
      initGoogle();
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>(
      'script[src="https://accounts.google.com/gsi/client"]',
    );
    if (existing) {
      existing.addEventListener("load", initGoogle);
      return () => existing.removeEventListener("load", initGoogle);
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.addEventListener("load", initGoogle);
    document.head.appendChild(script);
    return () => script.removeEventListener("load", initGoogle);
  }, [googleClientId]);

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {title ? (
          <div className={styles.titleBlock}>
            <h1 className={styles.title}>{title}</h1>
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          </div>
        ) : (
          logo && (
            <div className={styles.logoContainer}>
              <img src={logo} alt="Logo" className={styles.logo} />
            </div>
          )
        )}
        {error && <div className={styles.error}>{error}</div>}
        <div className={styles.formGroup}>
          <label>{t("login.username")}</label>
          <InputIcon
            icon={UserRound}
            placeholder={t("login.username")}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>
        <div className={styles.formGroup}>
          <label>{t("login.password")}</label>
          <InputIcon
            icon={LockKeyhole}
            type={showPassword ? "text" : "password"}
            placeholder={t("login.password")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            rightIcon={showPassword ? EyeOff : Eye}
            onRightIconClick={() => setShowPassword((v) => !v)}
          />
        </div>
        <Link className={styles.forgotPassword} to="/ask-password">
          {t("login.askPassword.link")}
        </Link>
        <button type="submit" className={styles.button} onClick={handleLogin}>
          {t("login.submit")}
        </button>
        {googleClientId && (
          <>
            <div className={styles.divider}>
              <span>{t("login.or")}</span>
            </div>
            <div ref={googleButtonRef} className={styles.googleButton} />
          </>
        )}
        {footerLinkHref && (
          <div className={styles.footer}>
            {footerText && <span>{footerText}</span>}
            <a href={footerLinkHref} className={styles.footerLink}>
              {footerLinkText}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

interface GoogleAccounts {
  accounts: {
    id: {
      initialize: (config: {
        client_id: string;
        callback: (response: { credential: string }) => void;
      }) => void;
      renderButton: (
        element: HTMLElement,
        options: {
          theme?: string;
          size?: string;
          width?: number;
          text?: string;
          locale?: string;
        },
      ) => void;
    };
  };
}
